import { useState } from "react";
import { formatDate } from "date-fns";

import { Button, SettingsList, SettingsListItem, Text, View } from "../base";
import { Dialog } from "../base/Dialog";
import { InputField } from "../base/InputField";
import { useAuth } from "../../hooks/useAuth";
import { OrganizationMember } from "../../redux/reducers/organizationMemberSlice";
import { OrganizationInvite } from "../../redux/reducers/organizationInvitesSlice";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";
import { ConfirmDialog } from "../ConfirmDialog";

type MembersCardProps = {
  members: OrganizationMember[];
  invites: OrganizationInvite[];
  onInviteUser: (email: string, role: "admin" | "member") => void;
  onCancelInvite: (inviteId: string) => void;
  onUpdateMemberRole: (memberId: string, role: "admin" | "member") => void;
  onRemoveMember: (memberId: string) => void;
  className?: string;
};

export const MembersCard = ({ members, invites, onInviteUser, onCancelInvite, onUpdateMemberRole, onRemoveMember, className }: MembersCardProps) => {
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [emailError, setEmailError] = useState("");
  const [showRemoveMemberDialog, setShowRemoveMemberDialog] = useState(false);
  const [removingMember, setRemovingMember] = useState<OrganizationMember | null>(null);
  const [showCancelInviteDialog, setShowCancelInviteDialog] = useState(false);
  const [cancelingInvite, setCancelingInvite] = useState<OrganizationInvite | null>(null);
  const { user } = useAuth();
  const currentMember = members.find((member) => member.userId === user?.id);
  const isOrganizationAdmin = user?.isAdmin || currentMember?.role === "admin" || currentMember?.role === "owner";

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleInvite = () => {
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    onInviteUser(email, role);
    setShowInviteDialog(false);
    setEmail("");
    setRole("member");
    setEmailError("");
  };

  const handleRemoveMemberClick = (member: OrganizationMember) => {
    setRemovingMember(member);
    setShowRemoveMemberDialog(true);
  };

  const confirmRemoveMember = () => {
    if (removingMember) {
      onRemoveMember(removingMember.id);
      setShowRemoveMemberDialog(false);
      setRemovingMember(null);
    }
  };

  const handleCancelInviteClick = (invite: OrganizationInvite) => {
    setCancelingInvite(invite);
    setShowCancelInviteDialog(true);
  };

  const confirmCancelInvite = () => {
    if (cancelingInvite) {
      onCancelInvite(cancelingInvite.id);
      setShowCancelInviteDialog(false);
      setCancelingInvite(null);
    }
  };

  return (
    <OrganizationSettingsTab
      title="Members"
      description="Manage members of your organization"
      onClickAction={() => setShowInviteDialog(true)}
      actionLabel={`${isOrganizationAdmin ? "Invite User" : undefined}`}
    >
      <SettingsList>
        {members.map((member) => (
          <SettingsListItem
            key={member.id}
            title={member.name || "Unknown"}
            subtitle={member.email}
            icon={member.name?.charAt(0)?.toUpperCase() || member.email?.charAt(0).toUpperCase()}
          >
            <View className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-2 w-full sm:w-auto">
              {isOrganizationAdmin && member.userId !== user?.id && member.role !== "owner" && (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => onUpdateMemberRole(member.id, member.role === "admin" ? "member" : "admin")}
                    className="sm:flex-shrink-0"
                  >
                    {member.role === "admin" ? "Demote to Member" : "Promote to Admin"}
                  </Button>
                  <Button
                    variant="ghost"
                    className="text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 sm:flex-shrink-0"
                    onClick={() => handleRemoveMemberClick(member)}
                  >
                    Remove
                  </Button>
                </>
              )}
              <Text
                variant="body"
                className={`px-2 py-1 !mx-4 rounded-full text-xs font-medium ${member.role === "owner" ? "bg-brand text-white" : "bg-gray-100 dark:bg-gray-700"}`}
              >
                {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
              </Text>
            </View>
          </SettingsListItem>
        ))}
        {invites?.map((invite) => (
          <SettingsListItem
            key={invite.id}
            title={invite.email}
            subtitle={`Invited as ${invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}`}
            icon="invite"
          >
            <View className="flex items-center space-x-2">
              <Text
                variant="body"
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                Expires on {formatDate(invite.expiresAt, "MM/dd/yyyy")}
              </Text>
              {isOrganizationAdmin && (
                <Button
                  variant="ghost"
                  className="text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300"
                  onClick={() => handleCancelInviteClick(invite)}
                >
                  Cancel Invite
                </Button>
              )}
            </View>
          </SettingsListItem>
        ))}
      </SettingsList>

      {/* Invite User Dialog */}
      <Dialog
        isOpen={showInviteDialog}
        onClose={() => {
          setShowInviteDialog(false);
          setEmail("");
          setRole("member");
          setEmailError("");
        }}
        title="Invite User"
      >
        <View className="p-4 space-y-4">
          <InputField
            label="Email"
            placeholder="Enter email address"
            value={email}
            onChange={(value: string) => {
              setEmail(value);
              setEmailError("");
            }}
            error={emailError}
          />

          <View className="space-y-2">
            <Text
              variant="body"
              className="font-medium"
            >
              Role
            </Text>
            <View className="flex items-center space-x-4">
              <View className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="member"
                  name="role"
                  checked={role === "member"}
                  onChange={() => setRole("member")}
                  className="h-4 w-4 text-brand focus:ring-brand"
                />
                <label
                  htmlFor="member"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Member
                </label>
              </View>
              <View className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="admin"
                  name="role"
                  checked={role === "admin"}
                  onChange={() => setRole("admin")}
                  className="h-4 w-4 text-brand focus:ring-brand"
                />
                <label
                  htmlFor="admin"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Admin
                </label>
              </View>
            </View>
          </View>

          <View className="flex space-x-3 pt-2">
            <Button
              variant="brand"
              onClick={handleInvite}
              isDisabled={!email}
              className="w-full"
            >
              Send Invite
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setShowInviteDialog(false);
                setEmail("");
                setRole("member");
                setEmailError("");
              }}
              className="w-full"
            >
              Cancel
            </Button>
          </View>
        </View>
      </Dialog>

      {/* Remove Member Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showRemoveMemberDialog}
        onClose={() => {
          setShowRemoveMemberDialog(false);
          setRemovingMember(null);
        }}
        onConfirm={confirmRemoveMember}
        title="Remove Member"
        message={`Are you sure you want to remove ${removingMember?.name || removingMember?.email} from the organization?`}
      >
        <Text className="text-sm text-gray-600 dark:text-gray-400">
          This action cannot be undone. The member will lose access to the organization immediately.
        </Text>
      </ConfirmDialog>

      {/* Cancel Invite Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCancelInviteDialog}
        onClose={() => {
          setShowCancelInviteDialog(false);
          setCancelingInvite(null);
        }}
        onConfirm={confirmCancelInvite}
        title="Cancel Invitation"
        message={`Are you sure you want to cancel the invitation for ${cancelingInvite?.email}?`}
      >
        <Text className="text-sm text-gray-600 dark:text-gray-400">
          This action cannot be undone. The invitation will be cancelled and the link will no longer work.
        </Text>
      </ConfirmDialog>
    </OrganizationSettingsTab>
  );
};
